import { useMemo } from 'react';
import type { TreeConditionNode } from '@/components/tree-dnd/tree-dnd-types';
import { ConditionEditSheet } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet';
import { ConditionEditSheetTargetFooter } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet-target-footer';
import { ConditionField } from '@/components/tree-dnd/tree-dnd-conditions/condition-field';
import { ConditionNodeSummary } from '@/components/tree-dnd/tree-dnd-conditions/condition-node-summary';
import { DateFilterControl } from '@/components/tree-dnd/tree-dnd-conditions/date-filter-control';
import { MatchModeInlineHelp } from '@/components/tree-dnd/tree-dnd-conditions/match-mode-inline-help';
import {
  ProductMatchList,
  useProductMatchListOptions,
} from '@/components/tree-dnd/tree-dnd-conditions/product-match-list';
import { useConditionAudiencePreview } from '@/components/tree-dnd/tree-dnd-conditions/use-condition-audience-preview';
import { uncapitalize } from '@/components/tree-dnd/tree-dnd-conditions/condition-summary-format';
import {
  useDateFilterSummary,
  useMatchListSummary,
  useNaturalMatchListSummary,
} from '@/components/tree-dnd/tree-dnd-conditions/use-condition-summaries';
import { useConditionEditSheet } from '@/components/tree-dnd/use-condition-edit-sheet';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';

interface ViewedProductConditionProps {
  node: Extract<TreeConditionNode, { kind: 'viewedProduct' }>;
  onChange: (
    value: Extract<TreeConditionNode, { kind: 'viewedProduct' }>['args']
  ) => void;
}

export function ViewedProductCondition({
  node,
  onChange,
}: ViewedProductConditionProps) {
  const { LL, locale } = useI18nContext();
  const { open, onOpenChange } = useConditionEditSheet(node.id);
  const value = node.args;
  const productOptions = useProductMatchListOptions(value.products.ids);
  const productsSummary = useMatchListSummary(
    value.products,
    productOptions,
    LL.audiences.filters.conditionSummary.noProductsSelected()
  );
  const productsNaturalSummary = useNaturalMatchListSummary(
    value.products,
    productOptions,
    LL.audiences.filters.conditionSummary.noProductsSelected(),
    {
      manyAll: ({ count }) =>
        LL.audiences.filters.conditionSummary.listManyAllProducts({ count }),
      manyAny: ({ count }) =>
        LL.audiences.filters.conditionSummary.listManyAnyProducts({ count }),
    }
  );
  const whenSummary = useDateFilterSummary(value.when);
  const conditionLabel = LL.audiences.filters.conditions.viewedProduct();

  const filterSummary = useMemo(() => {
    const when = uncapitalize(whenSummary);
    if (node.negated) {
      return LL.audiences.filters.conditionSummary.viewedProductExcludeSummary({
        products: productsNaturalSummary,
        when,
      });
    }
    return LL.audiences.filters.conditionSummary.viewedProductIncludeSummary({
      products: productsNaturalSummary,
      when,
    });
  }, [LL, node.negated, productsNaturalSummary, whenSummary]);

  const hasSelectedProducts = value.products.ids.length > 0;
  const preview = useConditionAudiencePreview(
    node,
    open && hasSelectedProducts
  );

  return (
    <>
      <ConditionNodeSummary
        label={conditionLabel}
        detail={`${productsSummary} · ${whenSummary}`}
      />
      <ConditionEditSheet
        open={open}
        onOpenChange={onOpenChange}
        title={conditionLabel}
        description={LL.audiences.filters.conditionSummary.viewedProductDescription()}
        footerContent={
          <ConditionEditSheetTargetFooter
            summary={filterSummary}
            error={
              hasSelectedProducts
                ? undefined
                : LL.audiences.filters.editSheet.noProductsSelectedError()
            }
            targetedUserCount={
              preview.isError ? undefined : preview.targetedUserCount
            }
            totalUserCount={
              preview.isError ? undefined : preview.totalUserCount
            }
            isLoading={preview.isLoading}
            locale={locale as Locales}
          />
        }
      >
        <div className="flex flex-col gap-6">
          <ConditionField
            label={LL.audiences.filters.editSheet.productsLabel()}
            helpPlacement="before"
            helpText={
              <MatchModeInlineHelp
                match={value.products.match}
                onChange={(match) =>
                  onChange({
                    ...value,
                    products: { ...value.products, match },
                  })
                }
                before={LL.audiences.filters.conditionSummary.viewedProductMatchHelpBefore()}
                afterAny={LL.audiences.filters.conditionSummary.viewedProductMatchHelpAfterAny()}
                afterAll={LL.audiences.filters.conditionSummary.viewedProductMatchHelpAfterAll()}
              />
            }
          >
            <ProductMatchList
              value={value.products}
              onChange={(products) => onChange({ ...value, products })}
              allowAll={false}
            />
          </ConditionField>
          <ConditionField
            label={LL.audiences.filters.editSheet.whenLabel()}
            helpText={LL.audiences.filters.editSheet.viewedProductWhenHelp()}
          >
            <DateFilterControl
              value={value.when}
              onChange={(when) => onChange({ ...value, when })}
            />
          </ConditionField>
        </div>
      </ConditionEditSheet>
    </>
  );
}
